import isNil from 'lodash/isNil';
import { useState, type ReactNode } from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/base/popover';
import { Separator } from '@ui/components/base/separator';
import { MoreIcon } from '@ui/components/icons';
import {
  ACTIONS_MENU_CONTENT_CLASSNAME,
  ActionsMenuItems,
  type ActionsMenuAction,
  type ActionsMenuClickAction,
  type ActionsMenuSubMenu,
  type ActionsMenuSubMenuAction,
} from '@ui/components/menus/actionsMenuItems';
import { cn, stopPropagation } from '@ui/lib/utils';

interface ActionsMenuProps {
  actions: ActionsMenuAction[];
  trigger?: React.ReactElement;
  header?: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  contentClassName?: string;
  onOpenChange?: (open: boolean) => void;
}

const ActionsMenu = ({
  actions,
  trigger,
  header,
  align = 'end',
  side = 'bottom',
  contentClassName,
  onOpenChange,
}: ActionsMenuProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          trigger ?? (
            <Button variant={ButtonVariant.GHOST} size="icon-sm" aria-label="Open actions menu">
              <MoreIcon />
            </Button>
          )
        }
        onClick={stopPropagation}
      />
      <PopoverContent
        align={align}
        side={side}
        className={cn(ACTIONS_MENU_CONTENT_CLASSNAME, contentClassName)}
        onClick={stopPropagation}
      >
        {!isNil(header) ? (
          <>
            {header}
            <Separator className="my-1" />
          </>
        ) : null}
        <ActionsMenuItems actions={actions} onSelect={() => handleOpenChange(false)} />
      </PopoverContent>
    </Popover>
  );
};

export { ActionsMenu };
export type {
  ActionsMenuAction,
  ActionsMenuClickAction,
  ActionsMenuProps,
  ActionsMenuSubMenu,
  ActionsMenuSubMenuAction,
};
